import React, { useState } from 'react';
import { useConfirmDialog } from '../ui/ConfirmDialog';

// Calendar View Types
export const CALENDAR_VIEWS = {
  MONTH: 'month',
  WEEK: 'week', 
  LIST: 'list',
  AGENDA: 'agenda'
};

// Helper Functions
const formatMonthYear = (date) => {
  return date.toLocaleDateString('it-IT', { 
    month: 'long', 
    year: 'numeric' 
  }).replace(/^\w/, c => c.toUpperCase());
};

const formatWeekRange = (date) => {
  const startOfWeek = new Date(date);
  startOfWeek.setDate(date.getDate() - date.getDay() + 1); // Lunedì
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6); // Domenica
  
  return `${startOfWeek.getDate()} - ${endOfWeek.getDate()} ${endOfWeek.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}`;
};

// 📅 MONTH VIEW 2.0 - Enhanced Monthly Calendar
function MonthView({ currentDate, events, onDayClick, onEventClick }) {
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  
  // Calculate padding for first day (0 = Sunday, convert to Monday = 0)
  let startPadding = firstDay.getDay() - 1;
  if (startPadding < 0) startPadding = 6; // Sunday becomes 6
  
  const days = [];
  
  // Empty days at the beginning
  for (let i = 0; i < startPadding; i++) {
    days.push(null);
  }
  
  // Days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    days.push(day);
  }
  
  const getEventsForDay = (day) => {
    if (!day) return [];
    const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(event => event.date === dateString);
  };
  
  const getCategoryColor = (category) => {
    const colors = {
      'training': 'bg-green-500 border-green-300',
      'club_tournament': 'bg-blue-500 border-blue-300',
      'national_tournament': 'bg-red-500 border-red-300'
    };
    return colors[category] || 'bg-gray-500 border-gray-300';
  };

  const getCategoryBadge = (category) => {
    const badges = {
      'training': '🥅',
      'club_tournament': '🏆',
      'national_tournament': '🇮🇹'
    };
    return badges[category] || '📅';
  };

  return (
    <div className="p-6 max-h-96 overflow-y-auto">
      {/* Week Headers */}
      <div className="grid grid-cols-7 gap-2 mb-4">
        {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map(day => (
          <div key={day} className="text-center font-semibold text-gray-600 py-2">
            {day}
          </div>
        ))}
      </div>
      
      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-2">
        {days.map((day, index) => {
          const dayEvents = day ? getEventsForDay(day) : [];
          const dateString = day ? `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}` : null;
          const isToday = day && 
            new Date().getDate() === day && 
            new Date().getMonth() === month && 
            new Date().getFullYear() === year;

          return (
            <div
              key={index}
              className={`min-h-32 border-2 rounded-xl p-3 transition-all duration-300 ${
                day 
                  ? `cursor-pointer border-gray-200 bg-white hover:bg-green-50 hover:border-green-300 hover:shadow-md ${
                      isToday ? 'ring-2 ring-green-500 bg-green-50 border-green-400' : ''
                    }`
                  : 'border-transparent bg-gray-50'
              }`}
              onClick={() => day && dateString && onDayClick(dateString)}
            >
              {day && (
                <>
                  {/* Day Number */}
                  <div className={`text-center font-bold text-lg mb-2 ${
                    isToday ? 'text-green-700 bg-green-100 rounded-full w-8 h-8 flex items-center justify-center mx-auto' : 'text-gray-800'
                  }`}>
                    {day}
                  </div>
                  
                  {/* Events */}
                  <div className="space-y-1">
                    {dayEvents.slice(0, 3).map((event, eventIndex) => (
                      <div
                        key={event.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEventClick(event);
                        }}
                        className="group relative cursor-pointer"
                        title={`${event.name}${event.time ? ` - ${event.time}` : ''}${event.description ? `\n${event.description}` : ''}`}
                      >
                        <div className={`px-2 py-1 rounded-md text-xs font-medium text-white transition-all duration-200 hover:scale-105 hover:shadow-md ${getCategoryColor(event.category)}`}>
                          <div className="flex items-center space-x-1">
                            <span>{getCategoryBadge(event.category)}</span>
                            <span className="truncate flex-1">
                              {event.name.length > 10 ? `${event.name.substring(0, 10)}...` : event.name}
                            </span>
                          </div>
                          {event.time && (
                            <div className="text-xs opacity-90 mt-1">
                              {event.time}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    
                    {/* More Events Indicator */}
                    {dayEvents.length > 3 && (
                      <div className="text-xs text-gray-500 text-center py-1 bg-gray-100 rounded-md">
                        +{dayEvents.length - 3} altri eventi
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// 📊 WEEK VIEW - Timeline Style Calendar
function WeekView({ currentDate, events, onTimeSlotClick, onEventClick }) {
  const getWeekDays = (date) => {
    const startOfWeek = new Date(date);
    startOfWeek.setDate(date.getDate() - date.getDay() + 1); // Start from Monday
    
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const weekDays = getWeekDays(currentDate);
  const timeSlots = [];
  for (let hour = 8; hour <= 22; hour++) {
    timeSlots.push(`${hour.toString().padStart(2, '0')}:00`);
  }

  const getEventsForDayAndTime = (day, timeSlot) => {
    const dateString = day.toISOString().split('T')[0];
    return events.filter(event => 
      event.date === dateString && 
      (event.time === timeSlot || (!event.time && timeSlot === '09:00'))
    );
  };

  return (
    <div className="p-6 max-h-96 overflow-y-auto">
      <div className="grid grid-cols-8 gap-2">
        {/* Time Column */}
        <div className="space-y-2 pt-12">
          {timeSlots.map(time => (
            <div key={time} className="h-16 text-xs text-gray-500 text-right pr-2 flex items-center justify-end">
              {time}
            </div>
          ))}
        </div>

        {/* Day Columns */}
        {weekDays.map(day => {
          const isToday = day.toDateString() === new Date().toDateString();
          
          return (
            <div key={day.toISOString()} className="space-y-2">
              {/* Day Header */}
              <div className={`text-center py-3 rounded-lg font-semibold ${
                isToday ? 'bg-green-100 text-green-800' : 'bg-gray-50 text-gray-700'
              }`}>
                <div className="text-sm">{day.toLocaleDateString('it-IT', { weekday: 'short' }).toUpperCase()}</div>
                <div className={`text-lg ${isToday ? 'bg-green-600 text-white rounded-full w-8 h-8 flex items-center justify-center mx-auto mt-1' : ''}`}>
                  {day.getDate()}
                </div>
              </div>

              {/* Time Slots */}
              {timeSlots.map(timeSlot => {
                const slotEvents = getEventsForDayAndTime(day, timeSlot);
                
                return (
                  <div
                    key={timeSlot}
                    className="h-16 border border-gray-200 rounded-md p-1 cursor-pointer hover:bg-blue-50 transition-colors"
                    onClick={() => onTimeSlotClick(day.toISOString().split('T')[0], timeSlot)}
                  >
                    {slotEvents.map(event => (
                      <div
                        key={event.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEventClick(event);
                        }}
                        className="bg-green-500 text-white text-xs px-2 py-1 rounded mb-1 cursor-pointer hover:bg-green-600 transition-colors"
                        title={event.description}
                      >
                        <div className="font-medium truncate">{event.name}</div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// 📋 LIST VIEW - Searchable Event List
function ListView({ events, searchQuery, onEventClick }) {
  const groupEventsByMonth = (events) => {
    const groups = {};
    events.forEach(event => {
      const date = new Date(event.date);
      const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
      const monthName = date.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
      
      if (!groups[monthKey]) {
        groups[monthKey] = {
          name: monthName.charAt(0).toUpperCase() + monthName.slice(1),
          events: []
        };
      }
      groups[monthKey].events.push(event);
    });
    
    // Sort events within each group by date
    Object.values(groups).forEach(group => {
      group.events.sort((a, b) => new Date(a.date) - new Date(b.date));
    });
    
    return groups;
  };

  const groupedEvents = groupEventsByMonth(events);
  
  const getCategoryInfo = (category) => {
    const info = {
      'training': { label: '🥅 Allenamento', color: 'bg-green-100 text-green-800' },
      'club_tournament': { label: '🏆 Torneo Club', color: 'bg-blue-100 text-blue-800' },
      'national_tournament': { label: '🇮🇹 Torneo Nazionale', color: 'bg-red-100 text-red-800' }
    };
    return info[category] || { label: '📅 Evento', color: 'bg-gray-100 text-gray-800' };
  };

  if (events.length === 0) {
    return (
      <div className="p-12 text-center">
        <div className="text-6xl mb-4">📅</div>
        <h3 className="text-xl font-semibold text-gray-600 mb-2">Nessun evento trovato</h3>
        <p className="text-gray-500">
          {searchQuery ? 'Prova a modificare i filtri di ricerca' : 'Inizia aggiungendo il tuo primo evento!'}
        </p>
      </div>
    );
  }

  return (
    <div className="p-6">
      {Object.entries(groupedEvents).map(([monthKey, group]) => (
        <div key={monthKey} className="mb-8">
          <h3 className="text-lg font-bold text-gray-800 mb-4 border-b border-gray-200 pb-2">
            📅 {group.name}
          </h3>
          
          <div className="space-y-3">
            {group.events.map(event => {
              const categoryInfo = getCategoryInfo(event.category);
              
              return (
                <div
                  key={event.id}
                  onClick={() => onEventClick(event)}
                  className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md hover:border-green-300 transition-all duration-200 cursor-pointer group"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h4 className="font-semibold text-gray-900 group-hover:text-green-700 transition-colors">
                          {event.name}
                        </h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${categoryInfo.color}`}>
                          {categoryInfo.label}
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-4 text-sm text-gray-600">
                        <span>📅 {new Date(event.date).toLocaleDateString('it-IT', { 
                          weekday: 'long', 
                          day: 'numeric', 
                          month: 'long' 
                        })}</span>
                        {event.time && (
                          <span>🕐 {event.time}</span>
                        )}
                      </div>
                      
                      {event.description && (
                        <p className="text-gray-600 text-sm mt-2 line-clamp-2">
                          {event.description}
                        </p>
                      )}
                    </div>
                    
                    <div className="text-gray-400 group-hover:text-green-500 transition-colors">
                      ➡️
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// Helper component for Agenda Event Section
function EventSection({ title, eventsList, icon, onEventClick }) {
  if (eventsList.length === 0) return null;
  
  return (
    <div className="mb-8">
      <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center space-x-2">
        <span>{icon}</span>
        <span>{title}</span>
        <span className="bg-gray-200 text-gray-700 px-2 py-1 rounded-full text-sm font-medium">
          {eventsList.length}
        </span>
      </h3>
      
      <div className="grid gap-3">
        {eventsList.slice(0, 5).map(event => (
          <div
            key={event.id}
            onClick={() => onEventClick(event)}
            className="bg-gradient-to-r from-white to-gray-50 border border-gray-200 rounded-lg p-4 hover:shadow-lg hover:from-green-50 hover:to-green-100 hover:border-green-300 transition-all duration-300 cursor-pointer group"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <div>
                  <h4 className="font-semibold text-gray-900 group-hover:text-green-700 transition-colors">
                    {event.name}
                  </h4>
                  <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                    <span>📅 {new Date(event.date).toLocaleDateString('it-IT')}</span>
                    {event.time && <span>🕐 {event.time}</span>}
                  </div>
                </div>
              </div>
              <div className="text-gray-400 group-hover:text-green-500 transition-colors">
                ➡️
              </div>
            </div>
          </div>
        ))}
        
        {eventsList.length > 5 && (
          <div className="text-center py-2 text-gray-500 text-sm">
            ... e altri {eventsList.length - 5} eventi
          </div>
        )}
      </div>
    </div>
  );
}

// 📌 AGENDA VIEW - Upcoming Events Focus
function AgendaView({ events, currentDate, onEventClick }) {
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  
  const thisWeekEnd = new Date(today);
  thisWeekEnd.setDate(today.getDate() + (7 - today.getDay()));
  
  const nextMonth = new Date(today);
  nextMonth.setMonth(today.getMonth() + 1);

  const categorizeEvents = (eventsList) => {
    const categories = {
      today: [],
      tomorrow: [],
      thisWeek: [],
      nextMonth: [],
      later: []
    };

    eventsList.forEach(event => {
      const eventDate = new Date(event.date);
      
      if (eventDate.toDateString() === today.toDateString()) {
        categories.today.push(event);
      } else if (eventDate.toDateString() === tomorrow.toDateString()) {
        categories.tomorrow.push(event);
      } else if (eventDate <= thisWeekEnd && eventDate > tomorrow) {
        categories.thisWeek.push(event);
      } else if (eventDate <= nextMonth && eventDate > thisWeekEnd) {
        categories.nextMonth.push(event);
      } else if (eventDate > nextMonth) {
        categories.later.push(event);
      }
    });

    return categories;
  };

  const categorizedEvents = categorizeEvents(events);

  return (
    <div className="p-6">
      <EventSection 
        title="Oggi" 
        eventsList={categorizedEvents.today} 
        icon="🔥" 
        onEventClick={onEventClick}
      />
      
      <EventSection 
        title="Domani" 
        eventsList={categorizedEvents.tomorrow} 
        icon="📋" 
        onEventClick={onEventClick}
      />
      
      <EventSection 
        title="Questa Settimana" 
        eventsList={categorizedEvents.thisWeek} 
        icon="📅" 
        onEventClick={onEventClick}
      />
      
      <EventSection 
        title="Prossimo Mese" 
        eventsList={categorizedEvents.nextMonth} 
        icon="🗓️" 
        onEventClick={onEventClick}
      />
      
      <EventSection 
        title="Più Avanti" 
        eventsList={categorizedEvents.later} 
        icon="🔮" 
        onEventClick={onEventClick}
      />
      
      {events.length === 0 && (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">📅</div>
          <h3 className="text-xl font-semibold text-gray-600 mb-2">Nessun evento programmato</h3>
          <p className="text-gray-500">Inizia pianificando i tuoi primi tornei e allenamenti!</p>
        </div>
      )}
    </div>
  );
}

// 👁️ EVENT VIEW MODAL - Read-only event details display
function EventViewModal({ event, onEdit, onDelete, onClose, showConfirm }) {
  if (!event) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'training': return '🏃';
      case 'club_tournament': return '🏆';
      case 'national_tournament': return '🥇';
      default: return '📅';
    }
  };

  const getCategoryName = (category) => {
    switch (category) {
      case 'training': return 'Allenamento';
      case 'club_tournament': return 'Torneo Club';
      case 'national_tournament': return 'Torneo Nazionale';
      default: return 'Evento';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">{getCategoryIcon(event.category)}</span>
              <div>
                <h2 className="text-xl font-bold">{event.name}</h2>
                <p className="text-green-100 text-sm">{getCategoryName(event.category)}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-green-200 p-2 rounded-full hover:bg-white hover:bg-opacity-20 transition-all"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="space-y-4">
            {/* Date and Time */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <span className="text-xl">📅</span>
                <div>
                  <p className="text-sm text-gray-600">Data</p>
                  <p className="font-semibold">{formatDate(event.date)}</p>
                </div>
              </div>
              
              {event.time && (
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <span className="text-xl">⏰</span>
                  <div>
                    <p className="text-sm text-gray-600">Ora</p>
                    <p className="font-semibold">{event.time}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Description */}
            {event.description && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-start space-x-3">
                  <span className="text-xl">📝</span>
                  <div className="flex-1">
                    <p className="text-sm text-gray-600 mb-1">Descrizione</p>
                    <p className="text-gray-800">{event.description}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Location */}
            {event.location && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-start space-x-3">
                  <span className="text-xl">📍</span>
                  <div className="flex-1">
                    <p className="text-sm text-gray-600 mb-1">Posizione</p>
                    <p className="text-gray-800">{event.location}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Attendees */}
            {event.attendees && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-start space-x-3">
                  <span className="text-xl">👥</span>
                  <div className="flex-1">
                    <p className="text-sm text-gray-600 mb-1">Partecipanti</p>
                    <p className="text-gray-800">{event.attendees}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Private Notes */}
            {event.notes && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start space-x-3">
                  <span className="text-xl">🔒</span>
                  <div className="flex-1">
                    <p className="text-sm text-yellow-700 mb-1">Note Private</p>
                    <p className="text-yellow-800">{event.notes}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Advanced Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {event.recurring && event.recurring !== 'none' && (
                <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
                  <span className="text-xl">🔄</span>
                  <div>
                    <p className="text-sm text-blue-600">Ricorrenza</p>
                    <p className="font-semibold text-blue-800 capitalize">{event.recurring}</p>
                  </div>
                </div>
              )}
              
              {event.reminder && event.reminder !== 'none' && (
                <div className="flex items-center space-x-3 p-3 bg-purple-50 rounded-lg">
                  <span className="text-xl">🔔</span>
                  <div>
                    <p className="text-sm text-purple-600">Promemoria</p>
                    <p className="font-semibold text-purple-800">{event.reminder}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="bg-gray-50 px-6 py-4 flex justify-between items-center">
          <div>
            {event.id && (
              <button
                onClick={() => {
                  showConfirm({
                    title: 'Elimina Evento',
                    message: 'Sei sicuro di voler eliminare questo evento dal calendario?',
                    confirmText: '🗑️ Elimina',
                    cancelText: 'Annulla',
                    variant: 'danger',
                    onConfirm: () => onDelete(event.id)
                  });
                }}
                className="text-red-600 hover:text-red-800 hover:bg-red-50 px-3 py-2 rounded-md font-medium transition-colors flex items-center space-x-2"
              >
                <span>🗑️</span>
                <span>Elimina</span>
              </button>
            )}
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors"
            >
              Chiudi
            </button>
            <button
              onClick={() => onEdit()}
              className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center space-x-2"
            >
              <span>✏️</span>
              <span>Modifica</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// 🎛️ Tab Button Component for Event Modal
function ModalTabButton({ tabId, label, icon, activeTab, setActiveTab }) {
  return (
    <button
      onClick={() => setActiveTab(tabId)}
      className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
        activeTab === tabId
          ? 'bg-green-100 text-green-700 border border-green-300'
          : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
      }`}
    >
      <span>{icon}</span>
      <span>{label}</span>
    </button>
  );
}

// 🎛️ PROFESSIONAL EVENT MODAL - Advanced Event Management
function ProfessionalEventModal({ event, onSave, onDelete, onClose, showConfirm }) {
  const [activeTab, setActiveTab] = useState('basic');
  const [formData, setFormData] = useState({
    name: event?.name || '',
    date: event?.date || new Date().toISOString().split('T')[0],
    time: event?.time || '',
    category: event?.category || 'training',
    description: event?.description || '',
    location: event?.location || '',
    recurring: event?.recurring || 'none',
    reminder: event?.reminder || 'none',
    attendees: event?.attendees || '',
    notes: event?.notes || ''
  });
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Il nome evento è obbligatorio';
    }
    
    if (!formData.date) {
      newErrors.date = 'La data è obbligatoria';
    }
    
    if (formData.time && !/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(formData.time)) {
      newErrors.time = 'Formato ora non valido (HH:MM)';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      onSave(formData);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-6 flex-shrink-0">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">
              {event?.id ? '✏️ Modifica Evento' : '➕ Nuovo Evento'}
            </h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl font-bold transition-colors"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 px-6 pt-4 flex-shrink-0">
          <div className="flex space-x-2 overflow-x-auto">
            <ModalTabButton tabId="basic" label="Dettagli Base" icon="📋" activeTab={activeTab} setActiveTab={setActiveTab} />
            <ModalTabButton tabId="advanced" label="Avanzate" icon="⚙️" activeTab={activeTab} setActiveTab={setActiveTab} />
            <ModalTabButton tabId="reminders" label="Promemoria" icon="🔔" activeTab={activeTab} setActiveTab={setActiveTab} />
          </div>
        </div>

        {/* Tab Content - Scrollable */}
        <div className="p-6 flex-1 overflow-y-auto">
          {/* Basic Tab */}
          {activeTab === 'basic' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome Evento *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Es: Torneo Regionale, Allenamento Tattico..."
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Data *
                  </label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
                      errors.date ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.date && <p className="text-red-500 text-sm mt-1">{errors.date}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ora
                  </label>
                  <input
                    type="time"
                    value={formData.time}
                    onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
                      errors.time ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.time && <p className="text-red-500 text-sm mt-1">{errors.time}</p>}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Categoria
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="training">🥅 Allenamento</option>
                  <option value="club_tournament">🏆 Torneo Club</option>
                  <option value="national_tournament">🇮🇹 Torneo Nazionale</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descrizione
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Dettagli dell'evento..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  📍 Posizione
                </label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Es: Sede Club, Palestra Comunale..."
                />
              </div>
            </div>
          )}

          {/* Advanced Tab */}
          {activeTab === 'advanced' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  🔄 Ricorrenza
                </label>
                <select
                  value={formData.recurring}
                  onChange={(e) => setFormData({ ...formData, recurring: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="none">Nessuna ricorrenza</option>
                  <option value="weekly">Settimanale</option>
                  <option value="monthly">Mensile</option>
                  <option value="custom">Personalizzata</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  👥 Partecipanti
                </label>
                <input
                  type="text"
                  value={formData.attendees}
                  onChange={(e) => setFormData({ ...formData, attendees: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Es: Mario, Luigi, Club Roma..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  📝 Note Private
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Note personali sull'evento..."
                />
              </div>
            </div>
          )}

          {/* Reminders Tab */}
          {activeTab === 'reminders' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  🔔 Promemoria
                </label>
                <select
                  value={formData.reminder}
                  onChange={(e) => setFormData({ ...formData, reminder: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="none">Nessun promemoria</option>
                  <option value="15min">15 minuti prima</option>
                  <option value="1hour">1 ora prima</option>
                  <option value="1day">1 giorno prima</option>
                  <option value="1week">1 settimana prima</option>
                </select>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <div className="text-blue-500 text-xl">💡</div>
                  <div>
                    <h4 className="font-medium text-blue-800 mb-1">Suggerimenti Promemoria</h4>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>• Allenamenti: 1 ora prima</li>
                      <li>• Tornei Club: 1 giorno prima</li>
                      <li>• Tornei Nazionali: 1 settimana prima</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions - Always Visible */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div>
              {event?.id && (
                <button
                  onClick={() => {
                    showConfirm({
                      title: 'Elimina Evento',
                      message: 'Sei sicuro di voler eliminare questo evento?',
                      confirmText: '🗑️ Elimina',
                      cancelText: 'Annulla',
                      variant: 'danger',
                      onConfirm: () => onDelete(event.id)
                    });
                  }}
                  className="text-red-600 hover:text-red-800 font-medium transition-colors"
                >
                  🗑️ Elimina Evento
                </button>
              )}
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={handleSave}
                className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors font-medium"
              >
                {event?.id ? '💾 Aggiorna' : '➕ Crea Evento'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Professional Calendar Container Component
function CalendarContainer({ events, onEventCreate, onEventUpdate, onEventDelete }) {
  const { showConfirm } = useConfirmDialog();
  const [currentView, setCurrentView] = useState(CALENDAR_VIEWS.MONTH);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showEventViewModal, setShowEventViewModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategories, setSelectedCategories] = useState(['all']);

  // Navigation handlers
  const handlePrevious = () => {
    const newDate = new Date(currentDate);
    if (currentView === CALENDAR_VIEWS.WEEK) {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setMonth(newDate.getMonth() - 1);
    }
    setCurrentDate(newDate);
  };

  const handleNext = () => {
    const newDate = new Date(currentDate);
    if (currentView === CALENDAR_VIEWS.WEEK) {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  // Filter events based on search and categories
  const getFilteredEvents = () => {
    let filtered = events;
    
    if (searchQuery) {
      filtered = filtered.filter(event => 
        event.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        event.description?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    if (!selectedCategories.includes('all')) {
      filtered = filtered.filter(event => 
        selectedCategories.includes(event.category)
      );
    }
    
    return filtered;
  };

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
      {/* Calendar Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          {/* Title and Navigation */}
          <div className="flex items-center space-x-4">
            <h2 className="text-2xl font-bold text-gray-900">
              📅 Calendario Tornei Pro
            </h2>
            <div className="flex items-center space-x-2">
              <button
                onClick={handlePrevious}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                ⬅️
              </button>
              
              <div className="text-lg font-semibold text-gray-700 min-w-48 text-center">
                {currentView === CALENDAR_VIEWS.WEEK
                  ? formatWeekRange(currentDate)
                  : formatMonthYear(currentDate)
                }
              </div>
              
              <button
                onClick={handleNext}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                ➡️
              </button>
              
              <button
                onClick={() => setCurrentDate(new Date())}
                className="px-3 py-1 text-sm bg-green-100 hover:bg-green-200 rounded-md transition-colors text-green-700 font-medium"
              >
                Oggi
              </button>
            </div>
          </div>

          {/* View Switcher and Actions */}
          <div className="flex items-center space-x-3">
            {/* View Switcher */}
            <div className="flex bg-gray-100 rounded-lg p-1 space-x-1">
              {[
                { id: CALENDAR_VIEWS.MONTH, label: 'Mese', icon: '📅', shortLabel: 'M' },
                { id: CALENDAR_VIEWS.WEEK, label: 'Settimana', icon: '📊', shortLabel: 'S' },
                { id: CALENDAR_VIEWS.LIST, label: 'Lista', icon: '📋', shortLabel: 'L' },
                { id: CALENDAR_VIEWS.AGENDA, label: 'Agenda', icon: '📌', shortLabel: 'A' }
              ].map(view => (
                <button
                  key={view.id}
                  onClick={() => setCurrentView(view.id)}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    currentView === view.id
                      ? 'bg-white shadow text-green-700 border border-green-200'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                  }`}
                >
                  <span className="hidden sm:inline">{view.icon} {view.label}</span>
                  <span className="sm:hidden">{view.shortLabel}</span>
                </button>
              ))}
            </div>
            
            <button
              onClick={() => {
                setSelectedEvent(null);
                setShowEventModal(true);
              }}
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors font-medium flex items-center space-x-2"
            >
              <span>➕</span>
              <span className="hidden sm:inline">Nuovo Evento</span>
            </button>
          </div>
        </div>

        {/* Search and Filters (for List/Agenda views) */}
        {(currentView === CALENDAR_VIEWS.LIST || currentView === CALENDAR_VIEWS.AGENDA) && (
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <input
                type="text"
                placeholder="🔍 Cerca eventi..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            
            <select 
              value={selectedCategories[0] || 'all'}
              onChange={(e) => setSelectedCategories(e.target.value === 'all' ? ['all'] : [e.target.value])}
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="all">🏷️ Tutte le Categorie</option>
              <option value="training">🥅 Allenamento</option>
              <option value="club_tournament">🏆 Torneo Club</option>
              <option value="national_tournament">🇮🇹 Torneo Nazionale</option>
            </select>
          </div>
        )}
      </div>
      
      <div className="calendar-content">
        {currentView === CALENDAR_VIEWS.MONTH && (
          <MonthView 
            currentDate={currentDate}
            events={getFilteredEvents()}
            onDayClick={(date) => {
              setSelectedEvent({ date });
              setShowEventModal(true);
            }}
            onEventClick={(event) => {
              setSelectedEvent(event);
              setShowEventViewModal(true);
            }}
          />
        )}
        
        {currentView === CALENDAR_VIEWS.WEEK && (
          <WeekView 
            currentDate={currentDate}
            events={getFilteredEvents()}
            onTimeSlotClick={(date, time) => {
              setSelectedEvent({ date, time });
              setShowEventModal(true);
            }}
            onEventClick={(event) => {
              setSelectedEvent(event);
              setShowEventViewModal(true);
            }}
          />
        )}
        
        {currentView === CALENDAR_VIEWS.LIST && (
          <ListView 
            events={getFilteredEvents()}
            searchQuery={searchQuery}
            onEventClick={(event) => {
              setSelectedEvent(event);
              setShowEventViewModal(true);
            }}
          />
        )}
        
        {currentView === CALENDAR_VIEWS.AGENDA && (
          <AgendaView 
            events={getFilteredEvents()}
            currentDate={currentDate}
            onEventClick={(event) => {
              setSelectedEvent(event);
              setShowEventViewModal(true);
            }}
          />
        )}
      </div>

      {/* Event View Modal (Read-only) */}
      {showEventViewModal && (
        <EventViewModal
          event={selectedEvent}
          onEdit={() => {
            setShowEventViewModal(false);
            setShowEventModal(true);
          }}
          onDelete={(eventId) => {
            onEventDelete(eventId);
            setShowEventViewModal(false);
            setSelectedEvent(null);
          }}
          onClose={() => {
            setShowEventViewModal(false);
            setSelectedEvent(null);
          }}
        />
      )}

      {/* Professional Event Modal */}
      {showEventModal && (
        <ProfessionalEventModal
          event={selectedEvent}
          showConfirm={showConfirm}
          onSave={(eventData) => {
            if (selectedEvent?.id) {
              onEventUpdate(selectedEvent.id, eventData);
            } else {
              onEventCreate(eventData);
            }
            setShowEventModal(false);
            setSelectedEvent(null);
          }}
          onDelete={(eventId) => {
            onEventDelete(eventId);
            setShowEventModal(false);
            setSelectedEvent(null);
          }}
          onClose={() => {
            setShowEventModal(false);
            setSelectedEvent(null);
          }}
        />
      )}
    </div>
  );
}

export default CalendarContainer;
export { MonthView, WeekView, ListView, AgendaView, EventViewModal, ProfessionalEventModal };
